import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Crosshair, Loader2, X, Plus } from "lucide-react";
import { toast } from "sonner";
import api from "@/services/api";
import { useAuth } from "@/context/AuthContext";
import TerrainMap, { STADIUM_SVG, isPaidTerrain } from "@/components/TerrainMap";

const DEFAULT_CENTER = [48.8566, 2.3522];

const CITY_COORDS = {
  paris: [48.8566, 2.3522], lyon: [45.7640, 4.8357], marseille: [43.2965, 5.3698],
  toulouse: [43.6047, 1.4442], nice: [43.7102, 7.2620], nantes: [47.2184, -1.5536],
  strasbourg: [48.5734, 7.7521], montpellier: [43.6108, 3.8767], bordeaux: [44.8378, -0.5792],
  lille: [50.6292, 3.0573], rennes: [48.1173, -1.6778], annemasse: [46.1939, 6.2356],
};

function resolveCity(city) {
  if (!city) return null;
  const key = city.toLowerCase().split(/[\s,]/)[0];
  return CITY_COORDS[key] || null;
}

function priceLabel(t) {
  return isPaidTerrain(t)
    ? { text: "PAYANT", color: "#eab308", bg: "rgba(234,179,8,0.15)", icon: "€" }
    : { text: "GRATUIT", color: "#22c55e", bg: "rgba(34,197,94,0.15)", icon: "" };
}

function bookingUrl(t) {
  const brand = (t.brand || "").toLowerCase();
  const name = (t.nom || "").toLowerCase();
  if (brand === "lefive" || name.includes("le five")) return "https://www.lefive.fr";
  if (brand === "urbansoccer" || name.includes("urbansoccer") || name.includes("urban soccer")) return "https://www.urbansoccer.fr";
  if (brand === "z5" || /\bz5\b/.test(name)) return "https://z5.zidane.fr";
  if (brand === "kipstadium" || name.includes("kipstadium")) return "https://www.kipstadium.com";
  // Fallback: Google search
  const q = encodeURIComponent(`${t.nom || ""} reservation`);
  return `https://www.google.com/search?q=${q}`;
}

export default function FindMatchPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [center, setCenter] = useState(null);
  const [recenterTrigger, setRecenterTrigger] = useState(0);
  const [terrains, setTerrains] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [permissionState, setPermissionState] = useState("idle");
  const [usingFallback, setUsingFallback] = useState(false);
  const [selectedTerrain, setSelectedTerrain] = useState(null);
  const [terrainFilter, setTerrainFilter] = useState("all"); // "all" | "free" | "paid"
  const lastBboxRef = useRef(null);

  // Depend on the city STRING (not the whole user object) so this memo stays
  // stable across unrelated AuthContext refreshes — prevents map reload loops.
  const profileCityCoords = useMemo(() => resolveCity(user?.city), [user?.city]);

  const visibleTerrains = useMemo(() => {
    if (terrainFilter === "all") return terrains;
    if (terrainFilter === "paid") return terrains.filter(isPaidTerrain);
    return terrains.filter((t) => !isPaidTerrain(t));
  }, [terrains, terrainFilter]);

  const loadByBbox = useCallback(async (bbox) => {
    if (!bbox) return;
    const last = lastBboxRef.current;
    if (last
      && Math.abs(last.minLat - bbox.minLat) < 0.001
      && Math.abs(last.maxLat - bbox.maxLat) < 0.001
      && Math.abs(last.minLng - bbox.minLng) < 0.001
      && Math.abs(last.maxLng - bbox.maxLng) < 0.001) return;
    lastBboxRef.current = bbox;
    setIsLoading(true);
    try {
      const data = await api.terrainsBbox({ ...bbox, limit: 1500 });
      setTerrains(data || []);
    } catch (e) {
      toast.error(e.message || "Erreur chargement terrains");
    } finally {
      setIsLoading(false);
    }
  }, []);

  const requestGeolocation = useCallback(() => {
    if (!navigator.geolocation) {
      setPermissionState("unsupported");
      const fallback = profileCityCoords || DEFAULT_CENTER;
      setCenter(fallback);
      setUsingFallback(true);
      setRecenterTrigger((n) => n + 1);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const coords = [pos.coords.latitude, pos.coords.longitude];
        setCenter(coords);
        setUsingFallback(false);
        setPermissionState("granted");
        setRecenterTrigger((n) => n + 1);
      },
      () => {
        setPermissionState("denied");
        const fallback = profileCityCoords || DEFAULT_CENTER;
        setCenter(fallback);
        setUsingFallback(true);
        setRecenterTrigger((n) => n + 1);
        toast.message("Position refusee — on centre sur ta ville", {
          description: user?.city || "Paris",
        });
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, [profileCityCoords, user?.city]);

  useEffect(() => {
    requestGeolocation();
  }, [requestGeolocation]);

  const handleCreateMatchHere = () => {
    if (!selectedTerrain) return;
    const label = selectedTerrain.ville
      ? `${selectedTerrain.nom} (${selectedTerrain.ville})`
      : selectedTerrain.nom;
    // Navigate to the dedicated creation page with the terrain prefilled.
    // (Replaces the previous global-modal flow to avoid stacking/perf issues.)
    navigate(`/creer-un-match?terrain=${encodeURIComponent(label)}`);
  };

  return (
    <div data-testid="find-match-page" style={{ minHeight: "100vh", backgroundColor: "#000", display: "flex", flexDirection: "column", position: "relative" }}>
      {/* Header (compact) */}
      <header style={{ padding: "10px 14px 8px", display: "flex", alignItems: "center", gap: "10px", backgroundColor: "rgba(0,0,0,0.95)", borderBottom: "1px solid #1a1a1a", zIndex: 1000, position: "sticky", top: 0 }}>
        <button data-testid="back-btn" onClick={() => navigate(-1)} style={{ padding: "8px", background: "none", border: "none", color: "#fff", cursor: "pointer" }}>
          <ArrowLeft size={20} />
        </button>
        <h1 className="font-heading" style={{ flex: 1, fontSize: "15px", fontWeight: 700, color: "#fff", margin: 0 }}>Trouver un terrain</h1>
        {isLoading && <Loader2 size={16} className="animate-spin" style={{ color: "#22c55e" }} />}
      </header>

      {/* Filter pills under the title */}
      <div data-testid="terrain-filter-bar" style={{
        position: "sticky", top: 48, zIndex: 999,
        display: "flex", gap: 8, padding: "8px 14px 10px",
        background: "rgba(0,0,0,0.95)", borderBottom: "1px solid #1a1a1a",
      }}>
        {[
          { key: "all",  label: "Tous" },
          { key: "free", label: "Gratuits" },
          { key: "paid", label: "Payants" },
        ].map((f) => {
          const active = terrainFilter === f.key;
          return (
            <button
              key={f.key}
              data-testid={`filter-${f.key}`}
              onClick={() => setTerrainFilter(f.key)}
              style={{
                height: 32, padding: "0 14px", borderRadius: 9999,
                fontSize: 12, fontWeight: 600, cursor: "pointer",
                background: active ? "#fff" : "transparent",
                color: active ? "#000" : "#9ca3af",
                border: active ? "none" : "1px solid #333",
                transition: "all 0.15s",
              }}
            >
              {f.label}
            </button>
          );
        })}
      </div>

      {/* Map fills the screen */}
      <div style={{ height: "calc(100vh - 178px)", position: "relative" }}>
        {center ? (
          <TerrainMap
            center={center}
            zoom={14}
            terrains={visibleTerrains}
            onBoundsChanged={loadByBbox}
            onTerrainClick={setSelectedTerrain}
            showMyPosition={!usingFallback}
            recenterTrigger={recenterTrigger}
            height="100%"
          />
        ) : (
          <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "#6b7280", fontSize: "13px", flexDirection: "column", gap: "12px" }}>
            <Loader2 size={20} className="animate-spin" />
            <span>Demande d'autorisation de localisation...</span>
          </div>
        )}

        {/* Floating GPS button bottom-right (above bottom nav) */}
        <button
          data-testid="recenter-gps-btn"
          onClick={requestGeolocation}
          aria-label="Recentrer sur ma position"
          style={{
            position: "fixed",
            right: "16px",
            bottom: "84px",
            width: "52px",
            height: "52px",
            borderRadius: "50%",
            backgroundColor: "#fff",
            border: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 4px 14px rgba(0,0,0,0.35)",
            cursor: "pointer",
            zIndex: 800,
            touchAction: "manipulation",
          }}
        >
          {isLoading ? <Loader2 size={22} color="#27ae60" className="animate-spin" /> : <Crosshair size={22} color="#27ae60" strokeWidth={2.5} />}
        </button>
      </div>

      {/* Bottom Sheet Drawer */}
      {selectedTerrain && (
        <div
          data-testid="terrain-drawer"
          style={{
            position: "fixed",
            left: 0, right: 0, bottom: 0,
            height: "auto",
            maxHeight: "60vh",
            backgroundColor: "#0a0a0a",
            borderTopLeftRadius: "20px",
            borderTopRightRadius: "20px",
            borderTop: "1px solid #222",
            boxShadow: "0 -8px 32px rgba(0,0,0,0.6)",
            padding: "16px 20px calc(24px + env(safe-area-inset-bottom, 0px))",
            zIndex: 10000,
            display: "flex", flexDirection: "column",
            animation: "slideUp 0.25s ease-out",
          }}
        >
          <div style={{ display: "flex", justifyContent: "center", marginBottom: "12px" }}>
            <div style={{ width: "44px", height: "4px", borderRadius: "2px", backgroundColor: "#333" }} />
          </div>
          <button
            data-testid="close-drawer-btn"
            onClick={() => setSelectedTerrain(null)}
            style={{ position: "absolute", top: "14px", right: "16px", background: "none", border: "none", color: "#6b7280", cursor: "pointer", padding: "4px" }}
          >
            <X size={20} />
          </button>

          <div style={{ flex: 1, overflow: "auto" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "8px" }}>
              <div style={{
                width: 40, height: 40, borderRadius: "50%",
                background: "linear-gradient(135deg,#22c55e 0%,#16a34a 100%)",
                border: "2px solid #fff",
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0,
                boxShadow: "0 3px 8px rgba(0,0,0,.55)",
              }} dangerouslySetInnerHTML={{ __html: STADIUM_SVG }} />
              <h2 className="font-heading" data-testid="drawer-name" style={{ fontSize: "20px", fontWeight: 800, color: "#fff", lineHeight: 1.2 }}>
                {selectedTerrain.nom}
              </h2>
            </div>

            <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginBottom: "14px" }}>
              {(() => {
                const pl = priceLabel(selectedTerrain);
                return (
                  <span data-testid="price-badge" style={{
                    fontSize: "12px", padding: "5px 12px", borderRadius: "999px",
                    backgroundColor: pl.bg, color: pl.color, fontWeight: 800, letterSpacing: "0.05em",
                    boxShadow: `0 0 0 1px ${pl.color}55`,
                  }}>
                    {pl.icon}{pl.icon ? " " : ""}{pl.text}
                  </span>
                );
              })()}
              <span style={{ fontSize: "11px", padding: "4px 10px", borderRadius: "999px", backgroundColor: "rgba(34,197,94,0.12)", color: "#22c55e", fontWeight: 600 }}>
                {selectedTerrain.type || "Public"}
              </span>
              {selectedTerrain.surface && (
                <span style={{ fontSize: "11px", padding: "4px 10px", borderRadius: "999px", backgroundColor: "rgba(255,255,255,0.06)", color: "#d1d5db" }}>
                  {selectedTerrain.surface}
                </span>
              )}
              {selectedTerrain.lit && (
                <span style={{ fontSize: "11px", padding: "4px 10px", borderRadius: "999px", backgroundColor: "rgba(234,179,8,0.12)", color: "#eab308" }}>
                  Eclaire
                </span>
              )}
            </div>

            {selectedTerrain.adresse && (
              <p style={{ fontSize: "13px", color: "#9ca3af", marginBottom: "16px", lineHeight: 1.5 }}>
                {selectedTerrain.adresse}
              </p>
            )}
          </div>

          {isPaidTerrain(selectedTerrain) && (
            <a
              data-testid="reserve-link"
              href={bookingUrl(selectedTerrain)}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                width: "100%",
                minHeight: "46px",
                borderRadius: "12px",
                background: "transparent",
                border: "1px solid rgba(234,179,8,0.5)",
                color: "#eab308",
                fontWeight: 700,
                fontSize: "13px",
                display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
                textDecoration: "none",
                marginBottom: "10px",
                padding: "10px 14px",
              }}
            >
              € Reserver sur le site officiel
            </a>
          )}

          <button
            data-testid="create-match-here-btn"
            onClick={handleCreateMatchHere}
            style={{
              width: "100%",
              minHeight: "54px",
              borderRadius: "14px",
              background: "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
              border: "none",
              color: "#fff",
              fontWeight: 800,
              fontSize: "15px",
              display: "flex", alignItems: "center", justifyContent: "center", gap: "10px",
              cursor: "pointer", touchAction: "manipulation",
              boxShadow: "0 6px 18px rgba(34,197,94,0.35)",
            }}
          >
            <Plus size={20} /> Creer un match ici
          </button>
        </div>
      )}

      <style>{`
        @keyframes slideUp {
          from { transform: translateY(100%); }
          to   { transform: translateY(0); }
        }
        .leaflet-container { font-family: Inter, sans-serif; }
        .leaflet-control-attribution { display: none !important; }
      `}</style>
    </div>
  );
}
